import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  AiAssistantAttachment,
  AiAssistantChatResponse,
  AiAssistantCitation,
  AiAssistantConversationMessage,
  AiAssistantEnvironment,
  AiAssistantService,
  AiAssistantToolCall,
} from '../../../services/ai-assistant/ai-assistant.service';
import { LocalStorageService } from '../../../services/localstorage/localstorage.service';
import { SessionService } from '../../../services/session/session.service';

interface PendingAiAssistantAttachment extends AiAssistantAttachment {
  id: string;
  size: number;
}

interface AiAssistantThreadMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  attachments: AiAssistantAttachment[];
  toolCalls: AiAssistantToolCall[];
  citations: AiAssistantCitation[];
  createdAt?: string;
  isError?: boolean;
}

interface CitationGroup {
  type: string;
  items: AiAssistantCitation[];
}

@Component({
  selector: 'app-ai-assistant',
  templateUrl: './ai-assistant.component.html',
  styleUrls: ['./ai-assistant.component.scss'],
})
export class AiAssistantComponent implements OnInit {
  protected readonly acceptedAttachmentTypes = '.pdf,.txt,.csv,text/plain,text/csv,application/pdf,image/*';
  private readonly inlineAttachmentMaxBytes = 5 * 1024 * 1024;
  private readonly allowedAttachmentExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'txt', 'csv'];

  protected readonly environment: AiAssistantEnvironment = 'qa';
  protected readonly promptStarters = [
    'Why is payroll onboarding failing for Acme in QA?',
    'Show recent QA errors related to UKG.',
    'What Jira tickets mention Radius ACH settlement failures?',
    'Find the Confluence runbook for 834 exports.',
  ];

  protected conversationId = '';
  protected draftMessage = '';
  protected messages: AiAssistantThreadMessage[] = [];
  protected pendingAttachments: PendingAiAssistantAttachment[] = [];
  protected restoring = false;
  protected sending = false;

  constructor(
    private readonly aiAssistantService: AiAssistantService,
    private readonly localStorageService: LocalStorageService,
    private readonly sessionService: SessionService,
    private readonly snackBar: MatSnackBar,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.restoreConversation();
  }

  protected get hasMessages(): boolean {
    return this.messages.length > 0;
  }

  protected get isBusy(): boolean {
    return this.restoring || this.sending;
  }

  protected get sendDisabled(): boolean {
    return this.isBusy || (!this.draftMessage.trim() && this.pendingAttachments.length === 0);
  }

  protected async sendMessage(): Promise<void> {
    const message = this.draftMessage.trim();
    if ((!message && this.pendingAttachments.length === 0) || this.sending) {
      return;
    }

    const requestAttachments = this.pendingAttachments.map((attachment) => this.toRequestAttachment(attachment));
    const displayAttachments = this.pendingAttachments.map((attachment) => this.toDisplayAttachment(attachment));

    const userMessage = this.createThreadMessage({
      id: this.buildLocalId('user'),
      role: 'user',
      content: message,
      attachments: displayAttachments,
      toolCalls: [],
      citations: [],
    });

    this.messages = [...this.messages, userMessage];
    this.draftMessage = '';
    this.pendingAttachments = [];
    this.sending = true;

    try {
      const response = await this.aiAssistantService.sendMessage({
        conversationId: this.conversationId || undefined,
        message,
        attachments: requestAttachments,
        context: {
          environment: this.environment,
        },
        client: {
          surface: 'teamadmin',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
        },
        stream: false,
      });

      this.applyConversationId(response.conversationId);
      this.messages = [...this.messages, this.mapResponseToAssistantMessage(response)];
    } catch (error: any) {
      this.messages = [
        ...this.messages,
        this.createThreadMessage({
          id: this.buildLocalId('assistant-error'),
          role: 'assistant',
          content: this.getErrorMessage(error),
          attachments: [],
          toolCalls: [],
          citations: [],
          isError: true,
        }),
      ];
    } finally {
      this.sending = false;
    }
  }

  protected async usePromptStarter(prompt: string): Promise<void> {
    this.draftMessage = prompt;
    await this.sendMessage();
  }

  protected startNewChat(): void {
    this.conversationId = '';
    this.messages = [];
    this.draftMessage = '';
    this.pendingAttachments = [];
    this.localStorageService.removeStorage(this.storageKey);
  }

  protected async handleComposerKeydown(event: KeyboardEvent): Promise<void> {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }

    event.preventDefault();
    await this.sendMessage();
  }

  protected trackByMessage(_: number, message: AiAssistantThreadMessage): string {
    return message.id;
  }

  protected trackByPendingAttachment(_: number, attachment: PendingAiAssistantAttachment): string {
    return attachment.id;
  }

  protected groupCitations(citations: AiAssistantCitation[]): CitationGroup[] {
    const groups = new Map<string, AiAssistantCitation[]>();

    citations.forEach((citation) => {
      const type = citation.type || 'source';
      const existing = groups.get(type) || [];
      existing.push(citation);
      groups.set(type, existing);
    });

    return Array.from(groups.entries()).map(([type, items]) => ({ type, items }));
  }

  protected formatSourceType(type: string): string {
    return type
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (value) => value.toUpperCase());
  }

  protected getCitationLabel(citation: AiAssistantCitation): string {
    return citation.label || citation.id || citation.url || 'Source';
  }

  protected getCitationHref(citation: AiAssistantCitation): string | null {
    return citation.url || null;
  }

  protected async onAttachmentSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files ? Array.from(input.files) : [];
    if (!files.length) {
      return;
    }

    const nextAttachments: PendingAiAssistantAttachment[] = [];

    for (const file of files) {
      const validationMessage = this.getAttachmentValidationMessage(file);
      if (validationMessage) {
        this.snackBar.open(validationMessage, 'Close', { duration: 4000 });
        continue;
      }

      try {
        nextAttachments.push(await this.readFileAsAttachment(file));
      } catch (error) {
        console.error('Failed to read AI Assistant attachment', error);
        this.snackBar.open(`Unable to read ${file.name}.`, 'Close', { duration: 3500 });
      }
    }

    if (nextAttachments.length > 0) {
      this.pendingAttachments = [...this.pendingAttachments, ...nextAttachments];
    }

    if (input) {
      input.value = '';
    }
  }

  protected removePendingAttachment(attachmentId: string): void {
    this.pendingAttachments = this.pendingAttachments.filter((attachment) => attachment.id !== attachmentId);
  }

  protected getAttachmentLabel(attachment: AiAssistantAttachment): string {
    return attachment.filename || attachment.fileId || attachment.fileUrl || 'Attachment';
  }

  protected getAttachmentSubtitle(attachment: AiAssistantAttachment): string {
    const parts = [
      attachment.mediaType || 'Unknown type',
      this.getReadableFileSize((attachment as PendingAiAssistantAttachment).size),
    ].filter(Boolean);
    return parts.join(' | ');
  }

  protected getAttachmentIcon(attachment: AiAssistantAttachment): string {
    const mediaType = (attachment.mediaType || '').toLowerCase();
    const filename = (attachment.filename || '').toLowerCase();

    if (mediaType.startsWith('image/')) {
      return 'image';
    }
    if (mediaType === 'application/pdf' || filename.endsWith('.pdf')) {
      return 'picture_as_pdf';
    }
    if (mediaType === 'text/csv' || filename.endsWith('.csv')) {
      return 'table_chart';
    }
    return 'description';
  }

  private async restoreConversation(): Promise<void> {
    const storedConversationId = this.localStorageService.getStorage(this.storageKey);
    if (!storedConversationId) {
      return;
    }

    this.restoring = true;

    try {
      const response = await this.aiAssistantService.getConversation(storedConversationId);
      this.applyConversationId(response.conversationId || storedConversationId);
      this.messages = response.messages
        .filter((message) => message.role === 'user' || message.role === 'assistant')
        .map((message) => this.mapConversationMessage(message));
    } catch (error: any) {
      this.localStorageService.removeStorage(this.storageKey);
      this.conversationId = '';
      if (this.getHttpStatus(error) !== 404) {
        this.snackBar.open('Unable to restore the previous AI Assistant chat.', 'Close', {
          duration: 3500,
        });
      }
      console.error('Failed to restore AI Assistant conversation', error);
    } finally {
      this.restoring = false;
    }
  }

  private mapConversationMessage(message: AiAssistantConversationMessage): AiAssistantThreadMessage {
    return this.createThreadMessage({
      id: message.id,
      role: message.role === 'assistant' ? 'assistant' : 'user',
      content: message.content,
      attachments: message.attachments,
      toolCalls: message.toolCalls,
      citations: message.citations,
      createdAt: message.createdAt,
    });
  }

  private mapResponseToAssistantMessage(response: AiAssistantChatResponse): AiAssistantThreadMessage {
    return this.createThreadMessage({
      id: response.messageId || this.buildLocalId('assistant'),
      role: 'assistant',
      content: response.answer || 'The assistant returned an empty response.',
      attachments: [],
      toolCalls: response.toolCalls,
      citations: response.citations,
    });
  }

  private createThreadMessage(message: AiAssistantThreadMessage): AiAssistantThreadMessage {
    return {
      ...message,
      attachments: message.attachments || [],
      toolCalls: message.toolCalls || [],
      citations: message.citations || [],
    };
  }

  private applyConversationId(conversationId?: string): void {
    if (!conversationId) {
      return;
    }

    this.conversationId = conversationId;
    this.localStorageService.setStorage(this.storageKey, conversationId);
  }

  private get storageKey(): string {
    const userId = this.sessionService.getUserId() || 'anonymous';
    return `team-admin.ai-assistant.${this.environment}.${userId}.conversation-id`;
  }

  private buildLocalId(prefix: string): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  }

  private async readFileAsAttachment(file: File): Promise<PendingAiAssistantAttachment> {
    const fileData = await this.readFileAsBase64(file);

    return {
      id: this.buildLocalId('attachment'),
      filename: file.name,
      mediaType: file.type || this.guessMediaType(file.name),
      fileData,
      size: file.size,
    };
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = typeof reader.result === 'string' ? reader.result : '';
        const [prefix, fileData] = result.split(',', 2);
        if (!prefix.startsWith('data:') || !fileData) {
          reject(new Error('Unexpected file reader result'));
          return;
        }
        resolve(fileData);
      };

      reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
      reader.readAsDataURL(file);
    });
  }

  private getAttachmentValidationMessage(file: File): string | null {
    const extension = this.getFileExtension(file.name);
    const mediaType = (file.type || '').toLowerCase();
    const extensionAllowed = this.allowedAttachmentExtensions.includes(extension);
    const mediaTypeAllowed =
      mediaType.startsWith('image/')
      || mediaType === 'application/pdf'
      || mediaType === 'text/plain'
      || mediaType === 'text/csv';

    if (!extensionAllowed && !mediaTypeAllowed) {
      return `Unsupported file type for ${file.name}. Use PDF, image, text, or CSV files.`;
    }

    if (file.size > this.inlineAttachmentMaxBytes) {
      return `${file.name} is too large for inline upload. Keep attachments under ${this.getReadableFileSize(this.inlineAttachmentMaxBytes)} until a file upload endpoint exists.`;
    }

    return null;
  }

  private toRequestAttachment(attachment: PendingAiAssistantAttachment): AiAssistantAttachment {
    return {
      filename: attachment.filename,
      mediaType: attachment.mediaType,
      fileData: attachment.fileData,
    };
  }

  private toDisplayAttachment(attachment: PendingAiAssistantAttachment): AiAssistantAttachment {
    return {
      filename: attachment.filename,
      mediaType: attachment.mediaType,
    };
  }

  private getFileExtension(filename: string): string {
    const parts = filename.toLowerCase().split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }

  private guessMediaType(filename: string): string {
    const extension = this.getFileExtension(filename);
    if (extension === 'pdf') {
      return 'application/pdf';
    }
    if (extension === 'csv') {
      return 'text/csv';
    }
    if (extension === 'txt') {
      return 'text/plain';
    }
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) {
      return extension === 'jpg' ? 'image/jpeg' : `image/${extension}`;
    }
    return 'application/octet-stream';
  }

  private getReadableFileSize(size?: number): string {
    if (!size && size !== 0) {
      return '';
    }
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  private getHttpStatus(error: any): number | undefined {
    const status = error?.status ?? error?.error?.status;
    return typeof status === 'number' ? status : undefined;
  }

  private getErrorMessage(error: any): string {
    return (
      error?.error?.message
      || error?.error?.error
      || error?.message
      || 'The assistant request failed. Please try again.'
    );
  }
}
